import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ArrowLeft, ArrowRight, Check, CircleAlert, CircleCheck, Clock3, Eye, FileCode2, History, Import, Rocket, Search, ShoppingCart } from 'lucide-react';
import { DOMAIN_CANDIDATES_MAX, domainCandidates, findMarket, isSiteOrderActive } from '@cp/shared';
import { Accordion, Banner, BrandIcon, Button, Dialog, Elapsed, Field, Input, LiveStatus, MarketName, PlatformIcon, Spinner, StatusPill, Steps, SubTabs } from '@/components/ui';
import { DomainPicker, MarketGrid, TemplateTile } from '@/components/geo/Pickers';
import { PurchaseLog } from '@/components/domains/PurchaseLog';
import { useRoles } from '@/hooks/useAuth';
import { useCancelPurchase, useDomainPurchase, useDomainPurchases, useStartPurchase } from '@/hooks/useDomainPurchases';
import { useFreeDomains } from '@/hooks/useGeo';
import { useCreateSiteOrder, useSiteOrder } from '@/hooks/useSiteOrders';
import { useTemplateFamilies } from '@/hooks/useTemplates';
import { errorCode, errorField, errorText, providerSaid } from '@/utils/errors';
import { idempotencyKey } from '@/utils/idempotency';
import { can } from '@/utils/permissions';
import { formatMoney } from '@/utils/purchases';
import { SITE_ORDER_STATE, orderBoughtDomain, orderEventText, orderFailureText, orderSteps } from '@/utils/site-orders';
import { isDomainName } from '@/utils/validation';
import type { DomainPurchaseView, SiteOrderView } from '@/types/api';

type Step = 'kind' | 'template' | 'identity' | 'domain' | 'review' | 'progress';

const STEPS: readonly { id: Step; label: string }[] = [
  { id: 'kind', label: 'Тип' },
  { id: 'template', label: 'Шаблон' },
  { id: 'identity', label: 'Название и гео' },
  { id: 'domain', label: 'Домен' },
  { id: 'review', label: 'Проверка' },
  { id: 'progress', label: 'Создание' },
];

/** The names typed in: one a line, or split by commas and spaces, each once. */
function namesOf(text: string): string[] {
  return [...new Set(text.split(/[\s,;]+/).map((name) => name.trim().toLowerCase()).filter(Boolean))];
}

function WizardSteps({ step }: { readonly step: Step }): ReactNode {
  const current = STEPS.findIndex((item) => item.id === step);
  return (
    <ol className="wizard-steps" aria-label="Шаги создания сайта">
      {STEPS.map((item, index) => (
        <li key={item.id} className={index < current ? 'wizard-step wizard-step-done' : index === current ? 'wizard-step wizard-step-current' : 'wizard-step'} aria-current={index === current ? 'step' : undefined}>
          <span className="wizard-step-number">{index < current ? <CircleCheck size={14} aria-hidden="true" /> : index + 1}</span>
          <span className="wizard-step-label">{item.label}</span>
        </li>
      ))}
    </ol>
  );
}

/** The chosen name in a check: its item, and the purchase it is bought through. */
interface Chosen {
  readonly purchaseId: string;
  readonly itemId: string;
  readonly domain: string;
}

/**
 * A new site, asked for in a few choices and made by the panel (ADR-0059):
 * what kind of project, which template, its name and market — always a flag —
 * and its domain, bought now or one already held. One button then buys the
 * domain, waits until its DNS is the panel's, and makes the site; every step
 * is followed live in the same window, which can be closed and reopened from
 * the Sites page at any time.
 */
export function NewSiteWizard({
  open,
  onClose,
  templateId = null,
  domain: presetDomain = null,
  orderId: followed = null,
  onOtherKind,
  onImport,
  onObserve,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Coming from a template: the kind and the template are chosen already. */
  readonly templateId?: string | null;
  /** A domain already held, handed over from a finished purchase. */
  readonly domain?: string | null;
  /** An order to follow: the window opens on its progress. */
  readonly orderId?: string | null;
  /** «Пустой WordPress» or «Сайт из файлов». */
  readonly onOtherKind?: () => void;
  readonly onImport?: () => void;
  readonly onObserve?: () => void;
}): ReactNode {
  const navigate = useNavigate();
  const roles = useRoles();
  const mayBuy = can(roles, 'domains.purchase');
  const families = useTemplateFamilies({ enabled: open });
  const builds = useMemo(() => (families.data ?? []).filter((family) => family.latest.build && !family.latest.deprecated), [families.data]);
  const freeDomains = useFreeDomains({ enabled: open });
  const purchases = useDomainPurchases({ enabled: open && mayBuy });

  const [step, setStep] = useState<Step>('kind');
  const [template, setTemplate] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [marketId, setMarketId] = useState<string | null>(null);
  const [tab, setTab] = useState<'buy' | 'mine'>('buy');
  const [candidates, setCandidates] = useState('');
  const [seededFor, setSeededFor] = useState('');
  const [checkId, setCheckId] = useState<string | null>(null);
  const [chosen, setChosen] = useState<Chosen | null>(null);
  const [mine, setMine] = useState<{ name: string; listed: boolean } | null>(null);
  const [orderId, setOrderId] = useState<string | null>(null);
  const key = useRef(idempotencyKey('site.order'));

  // A fresh start each time it opens: on the order it follows, or at the first step still to take.
  useEffect(() => {
    if (!open) return;
    setStep(followed ? 'progress' : templateId ? 'identity' : 'kind');
    setTemplate(templateId);
    setName('');
    setMarketId(null);
    setTab(presetDomain ? 'mine' : 'buy');
    setCandidates('');
    setSeededFor('');
    setCheckId(null);
    setChosen(null);
    setMine(presetDomain ? { name: presetDomain, listed: false } : null);
    setOrderId(followed);
    key.current = idempotencyKey('site.order');
  }, [open, followed, templateId, presetDomain]);

  const market = marketId ? findMarket(marketId) : null;
  const family = builds.find((item) => item.id === template) ?? null;
  const overview = purchases.data;
  // Buying needs the right, a 2d2s key, and the tags a bought domain gets so the panel may point it.
  const buyBlocked = !mayBuy
    ? 'Покупка доменов доступна владельцу и администраторам'
    : purchases.isPending
      ? null
      : !overview?.available
        ? 'Ключ 2d2s не настроен: покупка недоступна'
        : overview.tags.length === 0
          ? 'Не заданы теги доменов (TWOD2S_DOMAIN_TAGS): панель не сможет настроить DNS купленного домена'
          : null;
  const buying = tab === 'buy' && !buyBlocked;

  // The names to check follow the site's name and market until the owner edits them.
  const seed = `${name.trim()}|${marketId ?? ''}`;
  useEffect(() => {
    if (step !== 'domain' || seed === seededFor) return;
    setSeededFor(seed);
    setCandidates(domainCandidates(name, market).join('\n'));
  }, [step, seed, seededFor, name, market]);

  const check = useDomainPurchase(checkId);
  const checkView = check.data;
  const start = useStartPurchase({
    onSuccess: (view) => {
      setCheckId(view.id);
      setChosen(null);
    },
  });
  const cancel = useCancelPurchase(checkId ?? '');

  // A free name picked by default: the one 2d2s shortlisted.
  useEffect(() => {
    if (!checkView?.awaitingConfirmation || chosen) return;
    const first = checkView.items.find((item) => item.shortlisted && item.available === true) ?? checkView.items.find((item) => item.available === true);
    if (first) setChosen({ purchaseId: checkView.id, itemId: first.id, domain: first.domain });
  }, [checkView, chosen]);

  const create = useCreateSiteOrder({
    onSuccess: (view) => {
      setOrderId(view.id);
      setStep('progress');
    },
    // A refusal from the panel is final for this key; a lost answer is not — the same key then finds the same order.
    onError: (error) => {
      if (errorCode(error) !== null && errorCode(error) !== 'INTERNAL_ERROR') key.current = idempotencyKey('site.order');
    },
  });
  const order = useSiteOrder(step === 'progress' ? orderId : null);

  const typed = namesOf(candidates);
  const invalid = typed.filter((item) => !isDomainName(item));
  const domainName = buying ? (chosen?.domain ?? null) : mine && isDomainName(mine.name) ? mine.name : null;
  const short = buying && overview?.budget && checkView?.plannedCost !== null && checkView?.plannedCost !== undefined ? checkView.plannedCost > overview.budget.balance : false;

  /** A check whose names nobody bought is cancelled, so it does not linger in 2d2s. */
  const dropCheck = () => {
    if (checkView?.canCancel && !checkView.confirmationTaken && !checkView.siteOrderId && !orderId) cancel.mutate();
  };
  const close = () => {
    if (step !== 'progress') dropCheck();
    onClose();
  };
  const runCheck = () => {
    dropCheck();
    start.mutate({ candidates: typed, buy: 1, years: 1, autoRenew: true, privacy: true });
  };
  const submit = () => {
    if (!family || !market || !domainName) return;
    create.mutate({
      input: {
        templateId: family.id,
        market: market.id,
        name: name.trim(),
        domain: domainName,
        purchase: buying && chosen ? { purchaseId: chosen.purchaseId, itemId: chosen.itemId } : null,
      },
      key: key.current,
    });
  };

  const view = order.data;
  const titles: Record<Step, string> = {
    kind: 'Новый сайт',
    template: 'Шаблон',
    identity: 'Название и гео',
    domain: 'Домен',
    review: 'Всё готово',
    progress: view?.state === 'done' ? 'Сайт готов' : view?.state === 'failed' ? 'Сайт не создан' : `Создаём ${view?.domain ?? 'сайт'}`,
  };
  const descriptions: Record<Step, string> = {
    kind: 'Что создаём',
    template: 'Дизайн и структура сайта',
    identity: 'Как сайт называется и для кого он',
    domain: buyBlocked && tab === 'buy' ? 'Домен из уже купленных' : 'Купить новый или взять из своих',
    review: 'Панель купит домен, настроит DNS и создаст сайт',
    progress:
      view?.state === 'done'
        ? 'Закрыт от поисковиков до вашей проверки'
        : view?.state === 'failed'
          ? 'Что случилось и что делать — ниже'
          : 'Окно можно закрыть: создание продолжится, его видно на странице «Сайты»',
  };

  const price = checkView?.plannedCost ?? null;
  const footer = (() => {
    switch (step) {
      case 'kind':
        return <Button onClick={close}>Отмена</Button>;
      case 'template':
        return (
          <>
            <Button variant="ghost" icon={ArrowLeft} onClick={() => setStep('kind')}>
              Назад
            </Button>
            <Button variant="primary" icon={ArrowRight} disabled={!family} onClick={() => setStep('identity')}>
              Далее
            </Button>
          </>
        );
      case 'identity':
        return (
          <>
            {templateId ? null : (
              <Button variant="ghost" icon={ArrowLeft} onClick={() => setStep('template')}>
                Назад
              </Button>
            )}
            <Button variant="primary" icon={ArrowRight} disabled={!name.trim() || !market} onClick={() => setStep('domain')}>
              Далее
            </Button>
          </>
        );
      case 'domain':
        return (
          <>
            <Button variant="ghost" icon={ArrowLeft} onClick={() => setStep('identity')}>
              Назад
            </Button>
            <Button variant="primary" icon={ArrowRight} disabled={!domainName} onClick={() => setStep('review')}>
              Далее
            </Button>
          </>
        );
      case 'review':
        return (
          <>
            <Button variant="ghost" icon={ArrowLeft} disabled={create.isPending} onClick={() => setStep('domain')}>
              Назад
            </Button>
            <Button variant="primary" icon={buying ? ShoppingCart : Rocket} busy={create.isPending} disabled={short || !domainName} onClick={submit}>
              {buying ? `Купить домен и создать сайт${price !== null ? ` — ${formatMoney(price, checkView?.currency ?? 'USD')}` : ''}` : 'Создать сайт'}
            </Button>
          </>
        );
      case 'progress':
        return view?.state === 'done' && view.siteId ? (
          <>
            <Button onClick={onClose}>Закрыть</Button>
            <Button
              variant="primary"
              icon={ArrowRight}
              onClick={() => {
                onClose();
                navigate(`/sites/${view.siteId}`);
              }}
            >
              Открыть сайт
            </Button>
          </>
        ) : (
          <>
            {view?.siteId ? (
              <Button
                variant="ghost"
                onClick={() => {
                  onClose();
                  navigate(`/sites/${view.siteId}`);
                }}
              >
                Страница сайта
              </Button>
            ) : null}
            <Button onClick={onClose}>Закрыть</Button>
          </>
        );
    }
  })();

  return (
    <Dialog open={open} title={titles[step]} description={descriptions[step]} onClose={close} size="lg" footer={footer}>
      <div className="stack">
        <WizardSteps step={step} />

        {step === 'kind' ? (
          <div className="stack">
            <div className="pick-grid" role="group" aria-label="Тип проекта">
              <button
                type="button"
                className="pick-card kind-card"
                disabled={!families.isPending && builds.length === 0}
                onClick={() => setStep('template')}
              >
                <span className="kind-card-body">
                  <BrandIcon name="wordpress" size={28} />
                  <span className="strong">Сайт на WordPress из шаблона</span>
                  <span className="muted text-sm">
                    {!families.isPending && builds.length === 0
                      ? 'Шаблонов пока нет: страница готового сайта → «Ещё» → «Сделать шаблоном»'
                      : 'Дизайн и структура из шаблона, язык и гео — сразу. Домен, DNS и сервер панель берёт на себя'}
                  </span>
                </span>
              </button>
              <div className="pick-card kind-card kind-card-static">
                <span className="kind-card-body">
                  <Import size={28} aria-hidden="true" className="muted" />
                  <span className="strong">Подключить существующий сайт</span>
                  <span className="muted text-sm">Перенести WordPress в панель или только следить за сайтом</span>
                  <span className="row-tight row-wrap">
                    {onImport ? (
                      <Button size="sm" icon={Import} onClick={onImport}>
                        Импорт WordPress
                      </Button>
                    ) : null}
                    {onObserve ? (
                      <Button size="sm" icon={Eye} onClick={onObserve}>
                        Мониторинг
                      </Button>
                    ) : null}
                  </span>
                </span>
              </div>
            </div>
            {onOtherKind ? (
              <button type="button" className="link-button field-hint quiet-choice" onClick={onOtherKind}>
                <FileCode2 size={14} aria-hidden="true" />
                Пустой WordPress или сайт из файлов
              </button>
            ) : null}
          </div>
        ) : null}

        {step === 'template' ? (
          families.isPending ? (
            <Spinner label="Загрузка шаблонов…" />
          ) : (
            <div className="pick-grid" role="listbox" aria-label="Шаблон">
              {builds.map((item) => (
                <TemplateTile key={item.id} family={item} selected={item.id === template} onPick={() => setTemplate(item.id)} />
              ))}
            </div>
          )
        ) : null}

        {step === 'identity' ? (
          <div className="stack">
            <Field label="Название сайта" htmlFor="site-name" hint="Как сайт называется в панели. Из названия панель предложит домены">
              <Input id="site-name" value={name} onChange={(event) => setName(event.target.value)} placeholder="Berlin News" maxLength={160} autoFocus autoComplete="off" />
            </Field>
            <section className="stack stack-tight" aria-labelledby="site-market">
              <span id="site-market" className="field-label">
                Страна и язык
              </span>
              <MarketGrid value={marketId} onChange={(item) => setMarketId(item.id)} />
            </section>
          </div>
        ) : null}

        {step === 'domain' ? (
          <div className="stack">
            <SubTabs
              label="Домен"
              prefix="new-site-domain-"
              active={tab}
              onChange={setTab}
              tabs={[
                { id: 'buy', label: 'Купить новый', icon: ShoppingCart },
                { id: 'mine', label: 'Из моих доменов', icon: Check },
              ]}
            />
            {tab === 'buy' ? (
              buyBlocked ? (
                <Banner tone="warning" icon={CircleAlert}>
                  {buyBlocked}
                </Banner>
              ) : (
                <BuyDomain
                  candidates={candidates}
                  onCandidates={setCandidates}
                  invalid={invalid}
                  count={typed.length}
                  check={checkView}
                  checkUpdatedAt={check.dataUpdatedAt}
                  checkFailed={check.isRefetchError}
                  starting={start.isPending}
                  startError={start.isError ? (providerSaid(start.error) ?? errorText(start.error, 'Не удалось начать проверку')) : null}
                  chosen={chosen}
                  onChoose={setChosen}
                  onCheck={runCheck}
                />
              )
            ) : (
              <DomainPicker domains={freeDomains.data?.available ? freeDomains.data.domains : null} loading={freeDomains.isPending} value={mine} onChange={setMine} />
            )}
          </div>
        ) : null}

        {step === 'review' && family && market && domainName ? (
          <div className="stack">
            <dl className="summary">
              <div>
                <dt>Шаблон</dt>
                <dd className="platform-name">
                  <PlatformIcon platform="wordpress" />
                  {family.name} <span className="muted">· версия {family.latest.version}</span>
                </dd>
              </div>
              <div>
                <dt>Название</dt>
                <dd>{name.trim()}</dd>
              </div>
              <div>
                <dt>Страна и язык</dt>
                <dd>
                  <MarketName country={market.country} language={market.language} codes={false} />
                </dd>
              </div>
              <div>
                <dt>Домен</dt>
                <dd className="mono">
                  {domainName}
                  <span className="muted"> · и www</span>
                </dd>
              </div>
              {buying ? (
                <>
                  <div>
                    <dt>Покупка</dt>
                    <dd>
                      на 1 год · {formatMoney(price, checkView?.currency ?? 'USD')} по расчёту 2d2s
                      {overview?.budget ? <span className="muted"> · на балансе {formatMoney(overview.budget.balance, overview.budget.currency)}</span> : null}
                    </dd>
                  </div>
                  <div>
                    <dt>DNS</dt>
                    <dd>Зона Cloudflare и записи — автоматически, тег {overview?.tags.map((tag) => `«${tag}»`).join(', ')}</dd>
                  </div>
                </>
              ) : null}
              <div>
                <dt>Сервер</dt>
                <dd>Выбирается автоматически</dd>
              </div>
              <div>
                <dt>Индексация</dt>
                <dd>Закрыта до вашей проверки</dd>
              </div>
            </dl>
            {short ? <p className="field-error">Недостаточно средств на балансе 2d2s: пополните его и вернитесь.</p> : null}
            {buying && chosen && checkView && !checkView.items.find((item) => item.id === chosen.itemId)?.shortlisted ? (
              <p className="field-hint">Цена рассчитана 2d2s для другого свободного имени; для выбранного может отличаться.</p>
            ) : null}
            {!buying && mine && !mine.listed ? <p className="field-hint">Этого домена нет среди доменов панели: DNS для него настраивается вручную в 2d2s.</p> : null}
            {buying ? <p className="field-hint">Оплата спишется с баланса 2d2s сразу после нажатия. Отменить покупку после этого нельзя.</p> : null}
            {create.isError ? (
              <p className="field-error" role="alert">
                {errorCode(create.error) === 'DOMAIN_ALREADY_BOUND'
                  ? 'Домен уже привязан к другому сайту'
                  : errorField(create.error) === 'templateId'
                    ? 'У шаблона нет готовой сборки'
                    : errorField(create.error) === 'domain'
                      ? 'Это имя больше не свободно в проверке: проверьте доступность ещё раз'
                      : (providerSaid(create.error) ?? errorText(create.error, 'Не удалось создать сайт'))}
              </p>
            ) : null}
          </div>
        ) : null}

        {step === 'progress' ? (
          order.isPending ? (
            <Spinner label="Загрузка заказа…" />
          ) : view ? (
            <OrderProgress view={view} updatedAt={order.dataUpdatedAt} failed={order.isRefetchError} />
          ) : (
            <p className="field-error">{errorText(order.error, 'Заказ не найден')}</p>
          )
        ) : null}
      </div>
    </Dialog>
  );
}

/** The «Купить новый» tab: the names to check, 2d2s's answer name by name, and the one to buy. */
function BuyDomain({
  candidates,
  onCandidates,
  invalid,
  count,
  check,
  checkUpdatedAt,
  checkFailed,
  starting,
  startError,
  chosen,
  onChoose,
  onCheck,
}: {
  readonly candidates: string;
  readonly onCandidates: (text: string) => void;
  readonly invalid: readonly string[];
  readonly count: number;
  readonly check: DomainPurchaseView | undefined;
  readonly checkUpdatedAt: number;
  readonly checkFailed: boolean;
  readonly starting: boolean;
  readonly startError: string | null;
  readonly chosen: Chosen | null;
  readonly onChoose: (chosen: Chosen) => void;
  readonly onCheck: () => void;
}): ReactNode {
  const checking = check !== undefined && !check.awaitingConfirmation && !check.finished;
  const tooMany = count > DOMAIN_CANDIDATES_MAX;
  return (
    <div className="stack">
      <Field
        label="Варианты"
        htmlFor="new-site-candidates"
        {...(invalid.length > 0
          ? { error: `Не похоже на доменное имя: ${invalid.slice(0, 3).join(', ')}` }
          : tooMany
            ? { error: `Не больше ${DOMAIN_CANDIDATES_MAX} вариантов за раз` }
            : { hint: 'Из названия и страны. Можно изменить: по одному на строку' })}
      >
        <textarea id="new-site-candidates" className="input mono" rows={5} value={candidates} onChange={(event) => onCandidates(event.target.value)} spellCheck={false} />
      </Field>
      <div className="row-tight row-wrap">
        <Button icon={Search} busy={starting} disabled={count === 0 || invalid.length > 0 || tooMany || checking} onClick={onCheck}>
          {check ? 'Проверить снова' : 'Проверить доступность'}
        </Button>
        <span className="muted text-sm">Проверка бесплатна: оплата — только на последнем шаге</span>
      </div>
      {startError ? <p className="field-error">{startError}</p> : null}

      {check ? (
        <div className="stack stack-tight">
          <div className="row-between">
            <span className="field-label">{checking ? '2d2s проверяет имена' : check.awaitingConfirmation ? 'Выберите домен' : 'Проверка закончилась'}</span>
            <LiveStatus updatedAt={checkUpdatedAt} live={checking} failed={checkFailed} />
          </div>
          <div className="stack stack-tight" role="radiogroup" aria-label="Домен для покупки">
            {check.items.map((item) => {
              const free = item.available === true && check.awaitingConfirmation;
              const selected = chosen?.itemId === item.id;
              return (
                <label key={item.id} className={`domain-choice${selected ? ' domain-choice-selected' : ''}${free ? '' : ' domain-choice-off'}`}>
                  <input
                    type="radio"
                    name="new-site-domain"
                    checked={selected}
                    disabled={!free}
                    onChange={() => onChoose({ purchaseId: check.id, itemId: item.id, domain: item.domain })}
                  />
                  <span className="mono">{item.domain}</span>
                  <span className={`text-sm ${item.available === true ? 'tone-healthy' : 'muted'}`}>
                    {item.available === true ? 'свободен' : item.available === false ? (item.failure ?? 'занят') : 'проверяется…'}
                  </span>
                </label>
              );
            })}
          </div>
          {check.awaitingConfirmation ? (
            <span className="muted text-sm">
              Цена по расчёту 2d2s: {formatMoney(check.plannedCost, check.currency)} за 1 год
              {check.items.every((item) => item.available !== true) ? ' · свободных нет: измените варианты и проверьте снова' : ''}
            </span>
          ) : null}
          {check.failure ? <p className="field-error">{check.failure}</p> : null}
        </div>
      ) : null}
    </div>
  );
}

/** The order under way, every step of it, live — and why it stopped, if it did. */
function OrderProgress({ view, updatedAt, failed }: { readonly view: SiteOrderView; readonly updatedAt: number; readonly failed: boolean }): ReactNode {
  const active = isSiteOrderActive(view.state);
  const unknown = view.state === 'paying' && view.events.some((event) => event.code === 'payment_unknown');
  return (
    <div className="stack">
      <div className="row-between row-wrap">
        <span className="row-tight">
          <StatusPill map={SITE_ORDER_STATE} status={view.state} />
          <MarketName country={findMarket(view.market)?.country ?? null} language={findMarket(view.market)?.language ?? null} codes={false} />
          <span className="mono">{view.domain}</span>
        </span>
        <span className="row-tight text-sm muted">
          {active ? (
            <>
              <Clock3 size={14} aria-hidden="true" />
              <Elapsed since={view.createdAt} />
            </>
          ) : null}
          <LiveStatus updatedAt={updatedAt} live={active} failed={failed} />
        </span>
      </div>

      {unknown ? (
        <Banner tone="warning" icon={Clock3}>
          Оплата отправлена, но 2d2s её ещё не подтвердил. Повторной оплаты не будет: заказ продолжится, как только 2d2s ответит.
        </Banner>
      ) : null}
      {view.state === 'failed' ? (
        <Banner tone="danger" icon={CircleAlert}>
          <span>
            {orderFailureText(view)}
            {orderBoughtDomain(view) ? ' Домен куплен и остаётся в 2d2s.' : ''}
          </span>
        </Banner>
      ) : null}
      {view.state === 'done' ? (
        <p className="row-tight tone-healthy">
          <Check size={16} aria-hidden="true" />
          Сайт работает. Осталось проверить переводы и открыть его для поисковиков на странице сайта.
        </p>
      ) : null}

      <Steps label="Ход создания сайта" items={orderSteps(view)} />

      {view.purchase ? <PurchaseLog events={view.purchase.events} open={false} title="Ход покупки в 2d2s" /> : null}
      {view.events.length > 0 ? (
        <Accordion title={`Ход заказа · ${view.events.length}`} icon={History} open={false}>
          <ol className="purchase-log" reversed>
            {[...view.events].reverse().map((event, index) => (
              <li key={`${event.at}-${index}`}>
                <time className="numeric muted" dateTime={event.at}>
                  {new Date(event.at).toLocaleTimeString('ru-RU')}
                </time>
                <span>{orderEventText(event, view.writeTags)}</span>
              </li>
            ))}
          </ol>
        </Accordion>
      ) : null}
    </div>
  );
}
